// heartbeat.js — the touch-base interval owner (Brief 025).
//
// `crmTouchBase()` doubles as the session heartbeat (Brief 188). Its job here
// is single-purpose: while the tab is VISIBLE and a session is live, ping the
// server on a fixed cadence so a displaced or expired session is discovered
// within one interval instead of lingering until the learner clicks something.
//
// Cadence (Allen, 2026-09-14 — Brief 025 §CRM contract #4): every 60-90s per
// login, PLUS on tab focus, PLUS around each sync operation. This module owns
// only the PERIODIC leg; sync-engine.js owns the focus + around-each-sync legs,
// because those are driven by its own events.
//
// Page Visibility is load-bearing, not an optimisation (Gotcha #5): Chrome and
// Firefox throttle background-tab timers to ~1 minute or worse, so a hidden tab
// cannot be trusted to keep a 60s cadence. We stop the timer outright while
// hidden and fire an immediate catch-up beat on the way back — which is also
// the semantically right thing, since a hidden tab has no learner to bounce.
//
// DOM-free by construction: the ONLY browser API this module touches is
// setInterval/clearInterval, and the visibility signal arrives as explicit
// `pause()` / `resume()` calls from sync-engine. That keeps it unit-testable
// with fake timers and keeps CLAUDE.md Rule #1 clean.

/**
 * Periodic touch-base cadence. Allen's range is 60-90s; V1 picks the tight end
 * so a displaced session is discovered fast. ONE constant — tune here if server
 * load ever argues for 90s (Brief 025 §Out of scope: no adaptive interval in V1).
 */
export const HEARTBEAT_INTERVAL_MS = 60_000;

export class Heartbeat {
  /**
   * @param {object} opts
   * @param {() => unknown} opts.beat the touch-base to run; a returned promise is awaited-and-guarded
   * @param {number} [opts.intervalMs]
   * @param {{ setInterval: typeof setInterval, clearInterval: typeof clearInterval }} [opts.timers]
   */
  constructor({ beat, intervalMs = HEARTBEAT_INTERVAL_MS, timers }) {
    this._beat = beat;
    this._intervalMs = intervalMs;
    this._timers = timers ?? { setInterval: setInterval.bind(globalThis), clearInterval: clearInterval.bind(globalThis) };
    /** @type {any} */ this._handle = null;
    /** running == started AND not paused */
    this._started = false;
    this._paused = false;
  }

  /** @returns {boolean} whether the interval timer is currently armed */
  get running() {
    return this._handle !== null;
  }

  /** @returns {boolean} whether start() has been called without a matching stop() */
  get started() {
    return this._started;
  }

  /**
   * Start beating. Fires ONE beat immediately (so a fresh login confirms its
   * session right away rather than 60s later), then on the interval.
   * Idempotent — a second start() while running is a no-op.
   */
  start() {
    if (this._started) return;
    this._started = true;
    this._paused = false;
    this._arm();
    this._fire();
  }

  /** Stop entirely. Idempotent. After this, resume() will NOT re-arm. */
  stop() {
    this._started = false;
    this._paused = false;
    this._disarm();
  }

  /**
   * Pause for a hidden tab. Keeps `started` true so resume() can re-arm.
   * Idempotent, and a no-op when never started.
   */
  pause() {
    if (!this._started) return;
    this._paused = true;
    this._disarm();
  }

  /**
   * Resume from hidden. Re-arms the interval AND fires an immediate catch-up
   * beat — the tab may have been hidden across a displacement, and Allen's
   * contract explicitly asks for a beat on tab focus.
   * No-op when never started or when not paused.
   */
  resume() {
    if (!this._started || !this._paused) return;
    this._paused = false;
    this._arm();
    this._fire();
  }

  _arm() {
    if (this._handle !== null) return;
    this._handle = this._timers.setInterval(() => this._fire(), this._intervalMs);
  }

  _disarm() {
    if (this._handle === null) return;
    this._timers.clearInterval(this._handle);
    this._handle = null;
  }

  /**
   * Run one beat, fully guarded. A throwing/rejecting beat must never kill the
   * interval — the next tick should still try (a transient network blip is the
   * common case, and stopping the heartbeat on it would silently disable
   * displacement detection for the rest of the session).
   */
  _fire() {
    try {
      const r = this._beat();
      if (r && typeof (/** @type {any} */ (r).catch) === "function") {
        /** @type {Promise<void>} */ (r).catch((err) => console.warn("[heartbeat] beat rejected", err));
      }
    } catch (err) {
      console.warn("[heartbeat] beat threw", err);
    }
  }
}
